import { usePostDetails } from "@circle-react/contexts/postsDetailsContext";
import { Box } from "@circleco/compass/components/Box";
import { TextArea } from "@circleco/compass/components/TextArea";
import { TextInput } from "@circleco/compass/components/TextInput";
import { Typography } from "@circleco/compass/components/Typography";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { PostCanonicalUrlField } from "@/react/components/PostsV3/PostCanonicalUrlField";
import { useCurrentPostHelpers } from "@/react/components/PostsV3/useCurrentPostHelpers";
import { OpenGraphImageDropzone } from "@/react/components/QuickPostV3/Settings/OpenGraphImageDropzone";

import { requireImagePostFormPost } from "./helpers";
import { ImagePostSeoSectionSkeleton } from "./ImagePostSeoSectionSkeleton";
import type { ImagePostFormValues } from "./types";

const ImagePostSeoSettings = () => {
  const { register } = useFormContext<ImagePostFormValues>();

  return (
    <Box>
      <div className="flex flex-col gap-5">
        <Typography variant="label-sm">
          {t("quick_post_v3.settings.subsections.seo")}
        </Typography>
        <PostCanonicalUrlField validateOnChange />
        <TextInput
          {...register("meta_tag_attributes.meta_title")}
          label={t("meta_tag_attributes.meta_title")}
          labelTooltip={t("meta_tag_attributes.meta_title_description", {
            obj: "post",
          })}
          placeholder={t("quick_post_v3.settings.meta_title_placeholder")}
        />
        <TextArea
          {...register("meta_tag_attributes.meta_description")}
          label={t("meta_tag_attributes.meta_description")}
          labelTooltip={t("meta_tag_attributes.meta_description_description")}
          placeholder={t("quick_post_v3.settings.meta_description_placeholder")}
          rows={4}
        />
      </div>
    </Box>
  );
};

const ImagePostSocialSettings = () => {
  const { register } = useFormContext<ImagePostFormValues>();

  return (
    <Box>
      <div className="flex flex-col gap-5">
        <Typography variant="label-sm">
          {t("quick_post_v3.settings.subsections.social")}
        </Typography>
        <OpenGraphImageDropzone />
        <TextInput
          {...register("meta_tag_attributes.opengraph_title")}
          label={t("meta_tag_attributes.opengraph_title")}
          labelTooltip={t("meta_tag_attributes.opengraph_title_description", {
            obj: "post",
          })}
          placeholder={t("quick_post_v3.settings.opengraph_title_placeholder")}
        />
        <TextArea
          {...register("meta_tag_attributes.opengraph_description")}
          label={t("meta_tag_attributes.opengraph_description")}
          labelTooltip={t(
            "meta_tag_attributes.opengraph_description_description",
          )}
          placeholder={t(
            "quick_post_v3.settings.opengraph_description_placeholder",
          )}
          rows={4}
        />
      </div>
    </Box>
  );
};

export const ImagePostSeoSection = () => {
  const post = requireImagePostFormPost(useCurrentPostHelpers().post);
  const { postDetails } = usePostDetails(post.id);

  if (post.id && !postDetails) return <ImagePostSeoSectionSkeleton />;

  return (
    <>
      <ImagePostSeoSettings />
      <ImagePostSocialSettings />
    </>
  );
};
